import { useAtom } from 'jotai';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { authAtom } from '../atoms';
import { ChangeProjectStatusConfirmDialog, ImageDialog, Navbar } from '../components';
import ReportPage from './ReportPage';
import { ERROR_PAGES, TOAST_DURATION } from '../lib/constants';
import ReportsPage from './ReportsPage';
import LoginPage from './LoginPage';
import NewReportPage from './NewReportPage';
import NewUserPage from './NewUserPage';
import ProjectsPage from './ProjectsPage';
import NewProjectPage from './NewProjectPage';
import SettingsPage from './SettingsPage';
import ProjectPage from './ProjectPage';
import ResetPasswordPage from './ResetPasswordPage';
import { PATHS } from '../lib/paths';
import UsersPage from './UsersPage';
import UserPage from './UserPage';
import { Loading } from '../components/Loading';
import ErrorPage from './ErrorPage';
import { mutationLoadingAtom } from '../atoms/mutationLoadingAtom';
import { useIsLoggedIn } from '../hooks/useIsLoggedIn';

function LoggedInRoute(element: JSX.Element) {
  const loggedIn = useIsLoggedIn();
  const [auth] = useAtom(authAtom);

  if (!auth) return null;

  return loggedIn ? element : <Navigate to={PATHS.loginPath()} />;
}

function EmployeeLoggedInRoute(element: JSX.Element) {
  const isSuperAdmin = useIsLoggedIn({ isAdmin: true });
  const [auth] = useAtom(authAtom);
  if (!auth) return null;

  return isSuperAdmin ? element : <Navigate to={PATHS.loginPath()} />;
}

function LoggedOutRoute(element: JSX.Element) {
  const loggedIn = useIsLoggedIn();
  const [auth] = useAtom(authAtom);

  if (!auth) return null;

  return !loggedIn ? element : <Navigate to={PATHS.homePath()} />;
}

const routes = [
  {
    path: PATHS.loginPath(),
    element: () => LoggedOutRoute(<LoginPage />),
  },
  {
    path: PATHS.resetPasswordPath(':token'),
    element: () => LoggedOutRoute(<ResetPasswordPage />),
  },
  {
    path: PATHS.homePath(),
    element: () => LoggedInRoute(<ReportsPage />),
  },
  {
    path: PATHS.reportsPath(),
    element: () => LoggedInRoute(<ReportsPage />),
  },
  {
    path: PATHS.reportPath(':id'),
    element: () => LoggedInRoute(<ReportPage />),
  },
  {
    path: PATHS.newReportPath(),
    element: () => LoggedInRoute(<NewReportPage />),
  },
  {
    path: PATHS.newUserPath(),
    element: () => LoggedInRoute(<NewUserPage />),
  },
  {
    path: PATHS.projectsPath(),
    element: () => LoggedInRoute(<ProjectsPage />),
  },
  { path: PATHS.newProjectPath(), element: () => LoggedInRoute(<NewProjectPage />) },

  { path: PATHS.projectPath(':id'), element: () => LoggedInRoute(<ProjectPage />) },

  {
    path: PATHS.newProjectPath(),
    element: () => EmployeeLoggedInRoute(<NewProjectPage />),
  },
  { path: PATHS.settingsPath(), element: () => LoggedInRoute(<SettingsPage />) },
  { path: PATHS.usersPath(), element: () => EmployeeLoggedInRoute(<UsersPage />) },

  { path: PATHS.userPath(':id'), element: () => EmployeeLoggedInRoute(<UserPage />) },
];

export function Router() {
  const [mutationLoading] = useAtom(mutationLoadingAtom);
  const loggedIn = useIsLoggedIn();

  return (
    <>
      <Toaster toastOptions={{ duration: TOAST_DURATION }} />

      <div className="flex flex-col h-screen overflow-hidden">
        {mutationLoading && <Loading absolute />}
        {loggedIn && <Navbar />}
        <div className="h-full overflow-auto">
          <Routes>
            {routes.map(({ path, element: Element }) => (
              <Route
                key={path}
                path={path}
                element={
                  <Suspense fallback={<Loading />}>
                    <Element />
                  </Suspense>
                }
              />
            ))}
            {ERROR_PAGES.map((errorPage) => {
              return (
                <Route
                  key={errorPage.name}
                  element={
                    <ErrorPage
                      title={errorPage.title}
                      name={errorPage.name}
                      description={errorPage.description}
                    />
                  }
                  path={errorPage.path}
                />
              );
            })}
          </Routes>
        </div>
      </div>

      <ImageDialog />
      <ChangeProjectStatusConfirmDialog />
    </>
  );
}
